import { axios } from "../utils/axios.create";
import { instanceBo } from "../utils/axios.create";
import { TotalsResponse } from "../domain/totals.interface";

const getKPIData = async (userId: string): Promise<TotalsResponse> => {
	return axios
		.get(`/users/${userId}/totals`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export type DashboardDateRange =
	| "LAST_MONTH"
	| "LAST_QUARTER"
	| "LAST_YEAR"
	// SIMS-7002: B2D dashboard hides the date filter UI and asks the
	// backend for KPIs over the entire enrollment history.
	| "ALL_TIME";

export interface DashboardKPISummaryFilters {
	accountId: string;
	programId: string;
	orgUnitIds?: string[];
	providerId?: string;
	dateRange: DashboardDateRange;
}

export interface DashboardKPISummary {
	totalIncentivesEarned: number;
	totalIncentivesReceived: number;
	sowsSubmitted: number;
	medicationProofSubmitted: number;
}

export interface DashboardSowsProgressionFilters {
	accountId: string;
	programId: string;
	orgUnitIds?: string[];
	providerId?: string;
}

export interface SowsProgressionPoint {
	day: number;
	avgScore: number;
}

const getKPISummary = async (
	filters: DashboardKPISummaryFilters,
): Promise<DashboardKPISummary> => {
	const { accountId, programId, orgUnitIds, providerId, dateRange } = filters;

	const params: Record<string, string> = {
		programId,
		dateRange,
	};

	if (orgUnitIds && orgUnitIds.length > 0) {
		params.orgUnitIds = orgUnitIds.join(",");
	}

	if (providerId && providerId !== "all" && providerId !== "") {
		params.providerId = providerId;
	}

	// instanceBo baseURL already bakes in `/v1/accounts/{accountId}`.
	void accountId;
	return instanceBo
		.get("/dashboard/kpi-summary", { params })
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

const getSowsProgression = async (
	filters: DashboardSowsProgressionFilters,
): Promise<SowsProgressionPoint[]> => {
	const { accountId, programId, orgUnitIds, providerId } = filters;

	const params: Record<string, string> = { programId };

	if (orgUnitIds && orgUnitIds.length > 0) {
		params.orgUnitIds = orgUnitIds.join(",");
	}

	if (providerId && providerId !== "all" && providerId !== "") {
		params.providerId = providerId;
	}

	// instanceBo baseURL already bakes in `/v1/accounts/{accountId}`.
	void accountId;
	return instanceBo
		.get("/dashboard/sows-progression", { params })
		.then((res) => res.data.body as SowsProgressionPoint[])
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

export interface EnrollmentScorePoint {
	day: number;
	score: number;
}

export interface IncentiveComparisonPoint {
	day: number;
	earned: number;
	potential: number;
}

export interface EnrollmentStats {
	sowsProgression: EnrollmentScorePoint[];
	incentivesEarned: number;
	incentiveComparison: IncentiveComparisonPoint[];
}

const getEnrollmentStats = async (
	accountId: string,
	enrollmentId: string,
): Promise<EnrollmentStats> => {
	// instanceBo baseURL already bakes in `/v1/accounts/{accountId}`.
	void accountId;
	return instanceBo
		.get(`/dashboard/enrollments/${enrollmentId}/stats`)
		.then((res) => res.data.body as EnrollmentStats)
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

export const dashboardService = {
	getKPIData,
	getKPISummary,
	getSowsProgression,
	getEnrollmentStats,
};
